import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Loader2, Link2, Link2Off, AlertTriangle, ShieldCheck, Trash2 } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { useAuth } from '../../../src/contexts/AuthContext';
import { logUsageEvent } from '../../../src/lib/logger';
import { useToast } from '../../../src/contexts/ToastContext';
import { RateioUnidadeGeradora } from '../../../types';
import { UcParaRateio } from '../../../src/hooks/useRateioBalanceamento';
import { aprovarCadastroUc } from './cadastroUcActions';

interface UcSearchResult {
  id: number;
  numero_uc: string;
  outro_numero_uc: string | null;
  nome_cliente: string | null;
  situacao: string | null;
  consumo_medio_kwh: number | null;
  rateio_unidade_geradora_id: string | null;
  cadastro_aprovado: boolean;
}

interface VincularUCsModalProps {
  ug: RateioUnidadeGeradora;
  ucsVinculadas: UcParaRateio[];
  onClose: () => void;
  onVinculosAlterados: () => void;
}

const VincularUCsModal: React.FC<VincularUCsModalProps> = ({ ug, ucsVinculadas, onClose, onVinculosAlterados }) => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState<UcSearchResult[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [selecionadas, setSelecionadas] = useState<Set<number>>(new Set());
  const [pendenteConfirmacao, setPendenteConfirmacao] = useState<number | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [aprovandoId, setAprovandoId] = useState<number | null>(null);
  const [confirmandoLimpeza, setConfirmandoLimpeza] = useState(false);

  useEffect(() => {
    if (termo.trim().length < 2) {
      setResultados([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setBuscando(true);
      try {
        const termoLike = `%${termo.trim()}%`;
        const { data, error } = await supabase
          .from('unidades_consumidoras')
          .select('id, numero_uc, outro_numero_uc, nome_cliente, situacao, consumo_medio_kwh, rateio_unidade_geradora_id, cadastro_aprovado')
          .or(`numero_uc.ilike.${termoLike},outro_numero_uc.ilike.${termoLike},nome_cliente.ilike.${termoLike}`)
          .neq('situacao', 'Cancelada')
          .limit(50);
        if (error) throw error;
        setResultados((data || []) as UcSearchResult[]);
      } catch (error) {
        console.error('Error searching UCs', error);
        addToast('Erro ao buscar UCs.', 'error');
      } finally {
        setBuscando(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [termo]);

  const toggleSelecionada = (uc: UcSearchResult) => {
    if (!uc.cadastro_aprovado) return; // gate — precisa aprovar o cadastro antes de vincular
    setSelecionadas(prev => {
      const nova = new Set(prev);
      if (nova.has(uc.id)) {
        nova.delete(uc.id);
        return nova;
      }
      const jaVinculadaOutraUg = !!uc.rateio_unidade_geradora_id && uc.rateio_unidade_geradora_id !== ug.id;
      if (jaVinculadaOutraUg) {
        // Exige confirmação extra antes de trocar o vínculo — não seleciona ainda.
        setPendenteConfirmacao(uc.id);
        return nova;
      }
      nova.add(uc.id);
      return nova;
    });
  };

  const confirmarTrocaDeUg = (ucId: number) => {
    setSelecionadas(prev => new Set(prev).add(ucId));
    setPendenteConfirmacao(null);
  };

  const handleAprovarCadastro = async (uc: UcSearchResult) => {
    setAprovandoId(uc.id);
    try {
      await aprovarCadastroUc(uc.id);
      setResultados(prev => prev.map(r => (r.id === uc.id ? { ...r, cadastro_aprovado: true } : r)));
      addToast(`Cadastro da UC ${uc.numero_uc} aprovado.`, 'success');
      logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'rateio_simulador', acao: 'aprovar_cadastro_uc_rateio', origem: 'acao' });
    } catch (error: any) {
      console.error('Error approving UC cadastro', error);
      addToast(error?.message || 'Erro ao aprovar o cadastro da UC.', 'error');
    } finally {
      setAprovandoId(null);
    }
  };

  const handleVincular = async () => {
    if (selecionadas.size === 0) return;
    setSalvando(true);
    try {
      const { error } = await supabase
        .from('unidades_consumidoras')
        .update({ rateio_unidade_geradora_id: ug.id, rateio_vinculado_em: new Date().toISOString() })
        .in('id', Array.from(selecionadas));
      if (error) throw error;
      addToast(`${selecionadas.size} UC(s) vinculada(s) à UG.`, 'success');
      logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'rateio_simulador', acao: 'vincular_ucs_ug', origem: 'acao' });
      setSelecionadas(new Set());
      setPendenteConfirmacao(null);
      setTermo('');
      setResultados([]);
      onVinculosAlterados();
    } catch (error) {
      console.error('Error linking UCs', error);
      addToast('Erro ao vincular UCs.', 'error');
    } finally {
      setSalvando(false);
    }
  };

  const handleDesvincular = async (ucId: number) => {
    setSalvando(true);
    try {
      const { error } = await supabase
        .from('unidades_consumidoras')
        .update({ rateio_unidade_geradora_id: null })
        .eq('id', ucId);
      if (error) throw error;
      addToast('UC desvinculada.', 'success');
      logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'rateio_simulador', acao: 'desvincular_uc_ug', origem: 'acao' });
      onVinculosAlterados();
    } catch (error) {
      console.error('Error unlinking UC', error);
      addToast('Erro ao desvincular UC.', 'error');
    } finally {
      setSalvando(false);
    }
  };

  const handleLimparTodas = async () => {
    if (ucsVinculadas.length === 0) return;
    setSalvando(true);
    try {
      const { error } = await supabase
        .from('unidades_consumidoras')
        .update({ rateio_unidade_geradora_id: null })
        .in('id', ucsVinculadas.map(uc => uc.id));
      if (error) throw error;
      addToast(`${ucsVinculadas.length} UC(s) desvinculada(s).`, 'success');
      logUsageEvent({ userId: user?.id, modulo: 'gestao', funcionalidade: 'rateio_simulador', acao: 'desvincular_uc_ug', origem: 'acao' });
      setConfirmandoLimpeza(false);
      onVinculosAlterados();
    } catch (error) {
      console.error('Error clearing all linked UCs', error);
      addToast('Erro ao limpar as UCs vinculadas.', 'error');
    } finally {
      setSalvando(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700 shrink-0">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white font-poppins">Vincular UCs — {ug.nome}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={termo}
              onChange={e => setTermo(e.target.value)}
              placeholder="Buscar UC por número ou nome do titular..."
              className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg pl-9 pr-3 py-2"
            />
            {buscando && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" size={16} />}
          </div>

          {resultados.length > 0 && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 max-h-64 overflow-y-auto">
              {resultados.map(uc => {
                const jaVinculadaOutraUg = !!uc.rateio_unidade_geradora_id && uc.rateio_unidade_geradora_id !== ug.id;
                const aguardandoConfirmacao = pendenteConfirmacao === uc.id;
                return (
                  <div key={uc.id} className="flex items-center gap-3 p-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-900">
                    <input
                      type="checkbox"
                      checked={selecionadas.has(uc.id)}
                      onChange={() => toggleSelecionada(uc)}
                      disabled={!uc.cadastro_aprovado}
                      className="cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {uc.numero_uc} — {uc.nome_cliente || 'Sem nome'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Consumo médio: {uc.consumo_medio_kwh != null ? `${uc.consumo_medio_kwh} kWh` : '—'}
                      </p>
                    </div>
                    {!uc.cadastro_aprovado ? (
                      <button
                        type="button"
                        disabled={aprovandoId === uc.id}
                        onClick={() => handleAprovarCadastro(uc)}
                        className="flex items-center gap-1 text-xs font-medium text-white bg-primary hover:opacity-90 rounded-md px-2 py-1 disabled:opacity-60"
                      >
                        {aprovandoId === uc.id ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                        Aprovar Cadastro
                      </button>
                    ) : aguardandoConfirmacao ? (
                      <button
                        type="button"
                        onClick={() => confirmarTrocaDeUg(uc.id)}
                        className="flex items-center gap-1 text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md px-2 py-1"
                      >
                        <AlertTriangle size={14} /> Confirmar troca de UG
                      </button>
                    ) : jaVinculadaOutraUg ? (
                      <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 font-medium">
                        <AlertTriangle size={14} /> já vinculada a outra UG
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}

          {termo.trim().length >= 2 && !buscando && resultados.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">Nenhuma UC encontrada.</p>
          )}

          <div>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                UCs vinculadas a esta UG ({ucsVinculadas.length})
              </h3>
              {ucsVinculadas.length > 0 && (
                confirmandoLimpeza ? (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1">
                      <AlertTriangle size={14} /> Confirmar limpeza de {ucsVinculadas.length} UC(s)?
                    </span>
                    <button
                      type="button"
                      onClick={() => setConfirmandoLimpeza(false)}
                      disabled={salvando}
                      className="px-2 py-1 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={handleLimparTodas}
                      disabled={salvando}
                      className="flex items-center gap-1 px-2 py-1 rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-60"
                    >
                      {salvando ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      Confirmar
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmandoLimpeza(true)}
                    disabled={salvando}
                    className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-60"
                  >
                    <Trash2 size={14} /> Limpar UCs
                  </button>
                )
              )}
            </div>
            {ucsVinculadas.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma UC vinculada ainda.</p>
            ) : (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 max-h-48 overflow-y-auto">
                {ucsVinculadas.map(uc => (
                  <div key={uc.id} className="flex items-center justify-between p-3 text-sm">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{uc.numero_uc}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{uc.nome_cliente || '—'}</p>
                    </div>
                    <button
                      onClick={() => handleDesvincular(uc.id)}
                      disabled={salvando}
                      className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-60"
                    >
                      <Link2Off size={14} /> Desvincular
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-700 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Fechar
          </button>
          <button
            onClick={handleVincular}
            disabled={selecionadas.size === 0 || salvando}
            className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
          >
            {salvando ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />}
            Vincular {selecionadas.size > 0 ? `(${selecionadas.size})` : ''}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default VincularUCsModal;
